import { showConfirm } from '../components/ConfirmDialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiDeleteBandFile } from '../api';
import {
  uploadLightPlot, lightPlotPreviewUrl, lightPlotPreviewPages,
  sharedLightPlots, withSharedLightPlots,
} from '../lib/lightPlots';
import type { LightsConfig, TransportType, LightFixtureCategory, LightFixtureItem, LightPlotRef } from '../types';
import { LIGHT_FIXTURE_CATEGORIES, LIGHT_FIXTURE_DEFAULTS, createLightFixtureItem } from '../types';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import AutoTextarea from '../components/AutoTextarea';
import NumberPicker from '../components/NumberPicker';
import Button from '../components/ui/Button';
import { useSuggestionList } from '../hooks/useSuggestionList';
import { useFocusField } from '../hooks/useFocusField';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';

export default function ConfigLightsPage() {
  const { config, updateLights } = useConfig();

  // Capability-aware write gate (lot A2-CAP, 2026-09-25). Every write of this
  // page persists through `useConfig` -> PUT /api/bands/:id/rider/config, the
  // rider CONTENT: a globally `read-only` account keeps it ONLY through
  // `content.rider` (conditional pre-guard mount, server/index.js). NEVER the
  // blanket `canWrite` here — it would freeze the very crew member the
  // capability is for.
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { t } = useTranslation();
  const { activeProfileId } = useProfileContext();
  const [transport, setTransport] = useState<TransportType>('van');
  // Multi light plots: upload/thumbnail state (2026-10-06).
  const [plotBusy, setPlotBusy] = useState(false);
  const [plotError, setPlotError] = useState<string | null>(null);
  // Page count per fileId (from the preview route's X-Pdf-Pages header), for the
  // "N pages" badge. Thumbnails stay page 1. (2026-10-06)
  const [plotPages, setPlotPages] = useState<Record<number, number>>({});
  useFocusField(true);
  // Reorder the Lights sections to mirror the Rider Layout's `lights.*` order.
  // Non-layout sections (Video, Lights Plot) keep their default position.
  const layoutOrder = useLayoutOrder('lights');
  const { types: fixtureTypes, put: putFixtureTypes } = useSuggestionList('light-fixture-types');

  const cfg = config.lights[transport];
  // Shared light-plot list: the UNION of the 4 transport modes, deduplicated by
  // fileId. The UI (thumbnail + badge) and the rider PDF read THIS single list.
  // (2026-10-06, evolution B)
  const sharedPlots = sharedLightPlots(config.lights);
  const plotsKey = sharedPlots.map((p) => p.fileId).join(',');
  // Best-effort "N pages" badge: read each plot's page count from the preview
  // route's X-Pdf-Pages header. Re-runs only when the shared list changes.
  useEffect(() => {
    if (!activeProfileId || sharedPlots.length === 0) return;
    let cancelled = false;
    Promise.all(
      sharedPlots.map(async (p) => [Number(p.fileId), await lightPlotPreviewPages(activeProfileId, Number(p.fileId))] as const),
    )
      .then((entries) => { if (!cancelled) setPlotPages(Object.fromEntries(entries)); })
      .catch(() => { /* the badge is best-effort — never block the page */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId, plotsKey]);
  const fixtures = cfg.fixtures || {
    movingHeads: [], spots: [], ledBars: [], strobes: [],
    washes: [], blinders: [], other: [],
  };

  const handleChange = (key: keyof LightsConfig, value: LightsConfig[keyof LightsConfig]) => {
    updateLights({
      [transport]: { ...config.lights[transport], [key]: value },
    } as any);
  };

  // Edit one category's item list (quantity/type/label/notes), like the musician
  // backline editor. (2026-08-13 — fixtures became editable lists.)
  const updateFixture = (cat: LightFixtureCategory, next: LightFixtureItem[]) => {
    updateLights({ [transport]: { ...config.lights[transport], fixtures: { ...fixtures, [cat]: next } } } as any);
  };
  const updateFixtureItem = (cat: LightFixtureCategory, id: string, patch: Partial<LightFixtureItem>) => {
    updateFixture(cat, fixtures[cat].map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const addFixtureItem = (cat: LightFixtureCategory) => {
    updateFixture(cat, [...fixtures[cat], createLightFixtureItem()]);
  };
  const removeFixtureItem = (cat: LightFixtureCategory, id: string) => {
    updateFixture(cat, fixtures[cat].filter((it) => it.id !== id));
  };
  // Auto-add a fixture type typed by hand into the shared suggestion list.
  const ensureFixtureType = (t: string) => {
    const v = (t || '').trim();
    if (!v) return;
    if (!fixtureTypes.some((x) => x.toLowerCase() === v.toLowerCase())) {
      void putFixtureTypes([...fixtureTypes, v]);
    }
  };

  const populateAll = async (src: TransportType) => {
    const srcCfg = config.lights[src];
    if (!(await showConfirm(t('configLights.populateAllConfirm', { src })))) return;
    updateLights({ [transport]: { ...srcCfg } } as any);
  };

  const populateField = (key: keyof LightsConfig) => async (src: TransportType) => {
    const srcVal = config.lights[src][key];
    if (srcVal === cfg[key]) return;
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleChange(key, srcVal);
    }
  };

  // Each Rider Layout field (`lights.*`) is a reorderable section; sections
  // with no layout field (Video, Lights Plot) keep their default order
  // (appended after the layout-ordered sections via sortByLayoutOrder).
  const blocks = [
    {
      id: 'lights.console',
      render: () => (
        <div className="form-section" data-field-id="lights.console">
          <h2>{t('configLights.lightingEquipment')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configLights.lightingConsole')} current={transport} fieldId="lights.console" exampleText="GrandMA3, ETC EOS" onUseExample={(t) => handleChange('console', t)} onPopulateFrom={populateField('console')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.console}
              onChange={(e) => handleChange('console', e.target.value)}
              placeholder={t('configLights.consolePh')}
            />
          </ConfigField>
          <ConfigField disabled={!canEditContent} label={t('configLights.hazeMachine')} current={transport} fieldId="lights.haze" exampleText="Chauvet Hurricane 2000" onUseExample={(t) => handleChange('hazeMachine', t)} onPopulateFrom={populateField('hazeMachine')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.hazeMachine}
              onChange={(e) => handleChange('hazeMachine', e.target.value)}
              placeholder={t('configLights.hazePh')}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'lights.fixtures',
      render: () => (
        <div className="form-section" data-field-id="lights.fixtures">
          <h2>{t('configLights.fixtures')}</h2>
          <p style={{ marginTop: -8, marginBottom: 12, color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {t('configLights.fixturesHint')}
          </p>
          {LIGHT_FIXTURE_CATEGORIES.map(({ value: cat, label }) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong style={{ fontSize: '0.95rem' }}>{label}</strong>
                <Button disabled={!canEditContent} variant="secondary" size="sm" onClick={() => addFixtureItem(cat)}>{t('configLights.add')}</Button>
              </div>
              {fixtures[cat].length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: '0.8rem' }}>{t('configLights.none')}</p>
              ) : (
                <div className="backline-list">
                  {fixtures[cat].map((item) => (
                    <div key={item.id} className="backline-row" style={{ gridTemplateColumns: '60px 130px minmax(0,1fr) minmax(0,1fr) 32px' }}>
                      <NumberPicker disabled={!canEditContent}
                        value={item.quantity}
                        options={[1, 2, 3, 4, 5, 6, 8, 10, 12, 16]}
                        onChange={(v) => updateFixtureItem(cat, item.id, { quantity: v || 1 })}
                        className="backline-qty"
                      />
                      <input disabled={!canEditContent}
                        type="text"
                        list={`light-fixture-types-${cat}`}
                        value={item.type}
                        onChange={(e) => updateFixtureItem(cat, item.id, { type: e.target.value })}
                        onBlur={(e) => ensureFixtureType(e.target.value)}
                        placeholder={t('configLights.modelPh', { label })}
                        className="backline-type"
                      />
                      <AutoTextarea disabled={!canEditContent}
                        value={item.label}
                        onChange={(e) => updateFixtureItem(cat, item.id, { label: e.target.value })}
                        placeholder={t('configLights.descPh')}
                        className="backline-label"
                      />
                      <AutoTextarea disabled={!canEditContent}
                        value={item.notes}
                        onChange={(e) => updateFixtureItem(cat, item.id, { notes: e.target.value })}
                        placeholder={t('configLights.notesPh')}
                        className="backline-notes"
                      />
                      <Button disabled={!canEditContent} variant="ghost" size="sm" className="btn-danger-icon" onClick={() => removeFixtureItem(cat, item.id)} aria-label={t('configLights.removeItem', { label })}>🗑</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* datalist must live OUTSIDE the grid (a grid child would become a column) */}
          {LIGHT_FIXTURE_CATEGORIES.map(({ value: cat }) => (
            <datalist key={cat} id={`light-fixture-types-${cat}`}>
              {[...new Set([...LIGHT_FIXTURE_DEFAULTS[cat], ...fixtureTypes])].map((t) => <option key={t} value={t} />)}
            </datalist>
          ))}
        </div>
      ),
    },
    {
      id: 'lights.dmx',
      render: () => (
        <div className="form-section" data-field-id="lights.dmx">
          <h2>{t('configLights.dmxPower')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configLights.dmxLine')} current={transport} fieldId="lights.dmx" exampleText="DMX line from FOH to stage right" onUseExample={(t) => handleChange('dmxLine', t)} onPopulateFrom={populateField('dmxLine')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.dmxLine}
              onChange={(e) => handleChange('dmxLine', e.target.value)}
              placeholder={t('configLights.dmxPh')}
              rows={2}
            />
          </ConfigField>
          <ConfigField disabled={!canEditContent} label={t('configLights.powerReq')} current={transport} fieldId="lights.power" exampleText="separate line from sound" onUseExample={(t) => handleChange('powerNotes', t)} onPopulateFrom={populateField('powerNotes')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.powerNotes}
              onChange={(e) => handleChange('powerNotes', e.target.value)}
              placeholder={t('configLights.powerPh')}
              rows={2}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'lights.video',
      render: () => (
        <div className="form-section" data-field-id="lights.video">
          <h2>{t('configLights.video')}</h2>
          <div className="form-row rc-form-row">
            <ConfigField disabled={!canEditContent} label={t('configLights.videoFormat')} current={transport} fieldId="lights.video" exampleText="HDMI 2.1, DisplayPort 1.4, DVI-D" onUseExample={(t) => handleChange('videoFormat', t)} onPopulateFrom={populateField('videoFormat')}>
              <AutoTextarea disabled={!canEditContent}
                value={cfg.videoFormat}
                onChange={(e) => handleChange('videoFormat', e.target.value)}
                placeholder={t('configLights.videoFormatPh')}
              />
            </ConfigField>
            <ConfigField disabled={!canEditContent} label={t('configLights.videoResolution')} current={transport} exampleText="4K 60Hz, Full HD 1080p" onUseExample={(t) => handleChange('videoResolution', t)} onPopulateFrom={populateField('videoResolution')}>
              <AutoTextarea disabled={!canEditContent}
                value={cfg.videoResolution}
                onChange={(e) => handleChange('videoResolution', e.target.value)}
                placeholder={t('configLights.videoResPh')}
              />
            </ConfigField>
          </div>
        </div>
      ),
    },
    {
      id: 'lights.notes',
      render: () => (
        <div className="form-section" data-field-id="lights.notes">
          <h2>{t('configLights.additionalNotes')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configLights.notes')} current={transport} fieldId="lights.notes" exampleText="UV capability, wireless DMX, etc." onUseExample={(t) => handleChange('notes', t)} onPopulateFrom={populateField('notes')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={cfg.notes}
             onChange={(e) => handleChange('notes', e.target.value)}
             placeholder={t('configLights.notesPh2')}
             rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'lights.plot',
      render: () => {
        // Read the SHARED union; write it IDENTICALLY into all 4 modes so no
        // normalisation can drop it (soft migration: first save converges).
        const plots: LightPlotRef[] = sharedPlots;
        const setPlots = (next: LightPlotRef[]) =>
          updateLights(withSharedLightPlots(config.lights, next) as any);
        const onAddFiles = async (fileList: FileList | null) => {
          if (!fileList || fileList.length === 0 || !activeProfileId) return;
          setPlotError(null);
          setPlotBusy(true);
          try {
            const added: LightPlotRef[] = [];
            for (const file of Array.from(fileList)) {
              added.push(await uploadLightPlot(activeProfileId, file));
            }
            setPlots([...plots, ...added]);
          } catch (e: any) {
            setPlotError(e?.message || t('configLights.plotUploadFailed'));
          } finally {
            setPlotBusy(false);
          }
        };
        const removePlot = async (ref: LightPlotRef) => {
          if (!(await showConfirm(t('configLights.plotRemoveConfirm', { name: ref.name })))) return;
          // Best-effort: drop the stored file too (editor+ server-side). If it
          // fails the reference is still removed — an orphan file is harmless.
          try { await apiDeleteBandFile(activeProfileId ?? '', Number(ref.fileId)); }
          catch (e) { console.warn('[ConfigLightsPage] light plot file delete skipped:', e); }
          setPlots(plots.filter((p) => p.id !== ref.id));
        };
        const movePlot = (index: number, dir: -1 | 1) => {
          const next = [...plots];
          const target = index + dir;
          if (target < 0 || target >= next.length) return;
          [next[index], next[target]] = [next[target], next[index]];
          setPlots(next);
        };
        return (
          <div className="form-section" data-field-id="light-plot.image">
            <h2>{t('configLights.lightsPlot')}</h2>
            <ConfigField disabled={!canEditContent} label={t('configLights.lightsPlotImage')} current={transport} onPopulateFrom={populateField('imageData')} labelBlock>
              <div className="image-upload-zone">
                <input disabled={!canEditContent}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        handleChange('imageData', reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {cfg.imageData && (
                  <div className="image-preview">
                    <img src={cfg.imageData} alt={t('configLights.lightsPlotAlt')} />
                    <button disabled={!canEditContent}
                      type="button"
                      onClick={() => handleChange('imageData', '')}
                      className="btn btn-danger btn-small"
                    >
                      {t('configLights.remove')}
                    </button>
                  </div>
                )}
              </div>
            </ConfigField>

            {/* Multi light plots (2026-10-06): PDFs or images stored in the band
                file store, referenced here by small {fileId} refs. The order of
                this list is the order they appear in the rider PDF. */}
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ fontSize: '0.95rem' }}>{t('configLights.plotFiles')}</strong>
                  <p style={{ margin: '2px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>{t('configLights.plotFilesHint')}</p>
                </div>
                <label className="btn btn-secondary btn-small" style={{ cursor: canEditContent ? 'pointer' : 'not-allowed' }}>
                  {plotBusy ? t('configLights.plotUploading') : t('configLights.plotAddFile')}
                  <input
                    type="file"
                    multiple
                    disabled={!canEditContent || plotBusy}
                    accept="application/pdf,image/png,image/jpeg"
                    style={{ display: 'none' }}
                    onChange={(e) => { void onAddFiles(e.target.files); e.target.value = ''; }}
                  />
                </label>
              </div>
              {plotError && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', margin: '6px 0 0' }}>{plotError}</p>}
              {plots.length === 0 ? (
                <p style={{ margin: '8px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>{t('configLights.plotNone')}</p>
              ) : (
                <div className="light-plot-list" style={{ marginTop: 10 }}>
                  {plots.map((ref, index) => (
                    <div key={ref.id} className="light-plot-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                      <img
                        src={activeProfileId ? lightPlotPreviewUrl(activeProfileId, Number(ref.fileId)) : ''}
                        alt={ref.name}
                        loading="lazy"
                        style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}
                      />
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          <span style={{ fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ref.name}>{ref.name}</span>
                          {(plotPages[Number(ref.fileId)] || 1) > 1 && (
                            <span
                              className="light-plot-pages-badge"
                              style={{ flex: '0 0 auto', padding: '1px 7px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                            >
                              {t('configLights.plotPages', { count: plotPages[Number(ref.fileId)] })}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{ref.mime || '—'}</div>
                      </div>
                      <Button disabled={!canEditContent || index === 0} variant="ghost" size="sm" onClick={() => movePlot(index, -1)} aria-label={t('configLights.plotMoveUp')}>↑</Button>
                      <Button disabled={!canEditContent || index === plots.length - 1} variant="ghost" size="sm" onClick={() => movePlot(index, 1)} aria-label={t('configLights.plotMoveDown')}>↓</Button>
                      <Button disabled={!canEditContent} variant="ghost" size="sm" className="btn-danger-icon" onClick={() => { void removePlot(ref); }} aria-label={t('configLights.plotRemove')}>🗑</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  const sortedBlocks = sortByLayoutOrder(blocks, layoutOrder, (b) => b.id);

  return (
    <div className="page config-page" data-config-page="lights" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configLights.title"
        title={t('configLights.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {sortedBlocks.map((b) => b.render())}
      </div>
    </div>
  );
}
